import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
// prettier-ignore
import { Autocomplete, Typography, TextField, Button, DialogTitle, DialogContent, DialogActions, ThemeProvider, IconButton } from '@mui/material';
import { parse, format, isBefore } from 'date-fns';
import { EditableElementsRows } from './elements/EditableElementsRows.jsx';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { globalTheme } from '../shared/_theme.jsx';
import { rolesOptions } from '../shared/_enums.js';
import { FROM, TO, TEMPLATE, isElementInRule } from './elements/_constants';
import './blocked_parked.css';

export default class CreateResponsibilityForm extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array<*>} properties
     * @returns {Element}
     */

    static getReactComponent($) {
        const DATE_FORMAT = 'yyyy-MM-dd';
        return (props) => {
            const rule = useProperty($, 'Rule');
            const shortName = useProperty($, 'Short Name');
            const name = useProperty($, 'Name');
            const startDate = useProperty($, 'Start Date');
            const endDate = useProperty($, 'End Date');
            const defaultElementsValue = useProperty($, 'Default Elements Value');
            const [isValid, setIsValid] = React.useState(false);
            const [editedData, setEditedData] = React.useState([].concat(TEMPLATE));

            React.useEffect(() => {
                setIsValid(!!rule && !!shortName && !!name && !!startDate && !!endDate && isBefore(startDate, endDate));
            }, [rule, shortName, name, startDate, endDate]);

            React.useEffect(() => {
                $.set(
                    'Elements',
                    editedData.filter((row) => isElementInRule(row, rule))
                );
            }, [editedData, rule]);

            React.useEffect(() => {
                // applying default values when changing
                const newEditedData = [...editedData];
                if (typeof defaultElementsValue === 'object') {
                    for (const key in defaultElementsValue) {
                        const index = newEditedData.findIndex((row) => row.Element === key);
                        if (index !== -1) {
                            newEditedData[index].ExprLow = defaultElementsValue[key];
                        }
                    }
                }
                setEditedData(newEditedData);
            }, [defaultElementsValue]);

            // Update prop back to Draw
            const updateProp = (prop, value) => {
                $.set(prop, value);
            };

            // edit element handler
            const onEditElement = (event, index, key) => {
                const editedElement = Object.assign({}, editedData[index]);
                editedElement[key] = event.target.value;
                const nextData = [].concat(editedData);
                nextData.splice(index, 1, editedElement);
                setEditedData(nextData);
            };

            // duplicate element handler
            const onDuplicateElement = (index) => {
                const newElement = Object.assign({}, editedData[index]);
                newElement[FROM] = '';
                newElement[TO] = '';
                const nextData = [].concat(editedData);
                nextData.splice(index + 1, 0, newElement);
                setEditedData(nextData);
            };

            // remove element handler
            const onRemoveElement = (index) => {
                const nextData = [].concat(editedData);
                nextData.splice(index, 1);
                setEditedData(nextData);
            };

            // Buttons handlers
            const handleCancel = () => {
                $.trigger('On Cancel');
            };
            const handleSave = () => {
                $.trigger('On Save');
            };

            return (
                <>
                    <ThemeProvider theme={globalTheme}>
                        <div className="create-responsibility-dialog">
                            <DialogTitle>
                                Add Responsibility
                                <IconButton
                                    aria-label="close"
                                    onClick={handleCancel}
                                    sx={{
                                        position: 'absolute',
                                        right: 10,
                                        top: 10,
                                        color: (theme) => theme.palette.primary.contrastText,
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent style={{ padding: '32px 16px' }}>
                                <div className="create-responsibility-form">
                                    <div className="row">
                                        <Typography variant="caption">Responsibility Status</Typography>
                                        <Typography>Responsibility Not Defined</Typography>
                                    </div>

                                    <Autocomplete
                                        id="buyer-type"
                                        options={rolesOptions}
                                        getOptionLabel={(option) => option.role || ''}
                                        renderInput={(params) => <TextField {...params} label="Role" />}
                                        onChange={(e, v) => updateProp('Rule', v.rule)}
                                        isOptionEqualToValue={(option, value) => {
                                            return option.rule === value;
                                        }}
                                        value={rolesOptions.find((op) => op.rule === rule) || ''}
                                        fullWidth
                                    />

                                    <div className="row">
                                        <Typography variant="caption">Short Name</Typography>
                                        <TextField
                                            type="text"
                                            inputProps={{
                                                maxLength: 12,
                                            }}
                                            fullWidth
                                            value={shortName ?? ''}
                                            onChange={(event) => updateProp('Short Name', event.target.value)}
                                        ></TextField>
                                    </div>

                                    <div className="row">
                                        <Typography variant="caption">Name</Typography>
                                        <TextField
                                            type="text"
                                            inputProps={{
                                                maxLength: 40,
                                            }}
                                            fullWidth
                                            value={name ?? ''}
                                            onChange={(event) => updateProp('Name', event.target.value)}
                                        ></TextField>
                                    </div>

                                    <div className="row">
                                        <Typography variant="caption">Start Date</Typography>
                                        <TextField
                                            type="date"
                                            fullWidth
                                            value={startDate ? format(startDate, DATE_FORMAT) : ''}
                                            onChange={(event) =>
                                                updateProp(
                                                    'Start Date',
                                                    parse(event.target.value, DATE_FORMAT, Date.now())
                                                )
                                            }
                                        ></TextField>
                                    </div>
                                    <div className="row">
                                        <Typography variant="caption">End Date</Typography>
                                        <TextField
                                            type="date"
                                            fullWidth
                                            value={endDate ? format(endDate, DATE_FORMAT) : ''}
                                            onChange={(event) =>
                                                updateProp(
                                                    'End Date',
                                                    parse(event.target.value, DATE_FORMAT, Date.now())
                                                )
                                            }
                                            InputProps={{ inputProps: { min: startDate } }}
                                        ></TextField>
                                    </div>
                                </div>
                                <div className="responsibility-elements-grid">
                                    <div className="responsibility-elements-headers">
                                        <Typography>Element description</Typography>
                                        <Typography>From</Typography>
                                        <Typography>To</Typography>
                                        <div></div>
                                    </div>
                                    <EditableElementsRows
                                        data={editedData}
                                        onEditElement={onEditElement}
                                        onDuplicateElement={onDuplicateElement}
                                        onRemoveElement={onRemoveElement}
                                        rule={rule}
                                    ></EditableElementsRows>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="text" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button variant="contained" disabled={!isValid} onClick={handleSave}>
                                    Save
                                </Button>
                            </DialogActions>
                        </div>
                    </ThemeProvider>
                </>
            );
        };
    }
}

registerBrick('018af61b6e520e02db84', CreateResponsibilityForm);
